import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Popover,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import BoltIcon from "@mui/icons-material/Bolt";
import HouseIcon from "src/assets/icons/house.svg";
import useAuth from "src/context/AuthContext/useAuth";
import useSimApi from "src/hooks/useSimApi";

const SIMULATOR_TYPE = (process.env.REACT_APP_SIMULATOR_TYPE || "target").toUpperCase();

const NAV_ITEMS = [
  { label: "Home", path: "/", icon: <HouseIcon style={{ width: 18, height: 18 }} /> },
  { label: "Endpoints", path: "/endpoints", icon: <ApiIcon sx={{ fontSize: 18 }} /> },
  { label: "Request Logs", path: "/logs", icon: <ListAltIcon sx={{ fontSize: 18 }} /> },
  { label: "Call Engine", path: "/call-engine", icon: <BoltIcon sx={{ fontSize: 18 }} /> },
  { label: "Settings", path: "/settings", icon: <SettingsIcon sx={{ fontSize: 18 }} /> },
];

interface Connector {
  id: number;
  client_name: string;
}

const Layout: React.FC = () => {
  const { logout, user, connectorId, isSuperAdmin, selectedConnectorId, setSelectedConnectorId, idToken } = useAuth();
  const { request } = useSimApi();
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [loadedSimulatorName, setLoadedSimulatorName] = useState<string>("");

  const activeConnectorId = selectedConnectorId ?? connectorId;

  useEffect(() => {
    const updateTitleAndLoaded = async () => {
      // /api/v1/simulators returns the list of saved sims with `is_active` flag.
      // Prefer the entity name (Save-as-new label); fall back to the oauth_config
      // name from /api/v1/config for default-bootstrapped sims that have no
      // user-supplied name yet.
      try {
        const [simRes, cfgRes] = await Promise.all([
          request("/api/v1/simulators"),
          request("/api/v1/config"),
        ]);
        const sims = simRes.ok ? await simRes.json() : [];
        const cfg = cfgRes.ok ? await cfgRes.json() : null;
        const active = sims.find((s: any) => s.is_active);
        const entityName = (active?.name || "").trim();
        const oauthName = (cfg?.name || "").trim();
        const display = entityName || oauthName || "";
        setLoadedSimulatorName(display);
        document.title = display
          ? `Simulator ${display} (${SIMULATOR_TYPE})`
          : `Strawbay Simulator (${SIMULATOR_TYPE})`;
      } catch {
        // ignore — keep existing label
      }
    };

    updateTitleAndLoaded();
    window.addEventListener("simulator-config-saved", updateTitleAndLoaded);
    return () =>
      window.removeEventListener("simulator-config-saved", updateTitleAndLoaded);
  }, [activeConnectorId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoadingConnectors(true);
    fetch("/api/v1/connectors", {
      headers: { Authorization: `Bearer ${idToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const results: Connector[] = (data?.results || [])
          .filter((c: any) => c.is_active !== false)
          .map((c: any) => ({ id: Number(c.id), client_name: c.client_name }))
          .sort((a: Connector, b: Connector) =>
            a.client_name.localeCompare(b.client_name)
          );
        setConnectors(results);
      })
      .catch(() => {})
      .finally(() => setLoadingConnectors(false));
  }, [isSuperAdmin, idToken]);

  const initials = (user?.email || "?").slice(0, 1).toUpperCase();

  const itemStyle = (isActive: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 18px",
    color: "#FFFFFF",
    textDecoration: "none",
    fontFamily: "Maven Pro, sans-serif",
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    opacity: isActive ? 1 : 0.8,
    background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
    borderRadius: 6,
    marginBottom: 4,
  });

  const textStyle = {
    fontFamily: "Maven Pro, sans-serif",
    fontSize: 13,
    color: "#fff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };

  // Triangle rendered at body level so it's above the MUI Modal (z-index 1300)
  const triangle = anchorEl
    ? ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            top: anchorEl.getBoundingClientRect().top,
            left: anchorEl.getBoundingClientRect().left + anchorEl.getBoundingClientRect().width * 0.63,
            transform: "translateX(-50%)",
            width: "0px",
            height: "0px",
            borderTop: "10px solid white",
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            zIndex: 1400,
            pointerEvents: "none",
            transition: "opacity 198ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
          }}
        />,
        document.body
      )
    : null;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {triangle}

      {/* Sidebar */}
      <Box
        sx={{
          width: 232,
          flexShrink: 0,
          position: "fixed",
          top: 0,
          bottom: 0,
          left: 0,
          display: "flex",
          flexDirection: "column",
          backgroundImage: `url(${require("src/assets/images/Strawbay-rectangle.png")})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          zIndex: 100,
          overflow: "hidden",
        }}
      >
        {/* Logo */}
        <Box sx={{ px: "18px", pt: "50px", pb: "19px" }}>
          <img
            src={require("src/assets/images/Strawbay-logo.png")}
            style={{ height: 38, width: "auto" }}
          />
        </Box>

        {/* Admin connector switcher */}
        {isSuperAdmin && (
          <Box sx={{ px: "14px", pb: "12px" }}>
            {loadingConnectors ? (
              <CircularProgress size={14} sx={{ color: "#fff", ml: 1 }} />
            ) : (
              <Select
                size="small"
                value={activeConnectorId ?? ""}
                onChange={(e) =>
                  setSelectedConnectorId(e.target.value ? Number(e.target.value) : null)
                }
                displayEmpty
                sx={{
                  width: "100%",
                  color: "#fff",
                  fontFamily: "Maven Pro, sans-serif",
                  fontSize: 13,
                  bgcolor: "rgba(255,255,255,0.1)",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                  "& .MuiSvgIcon-root": { color: "#fff" },
                  "& .MuiSelect-select": { py: "6px", px: "10px" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 300, bgcolor: "#3b3580", color: "#fff" },
                  },
                }}
              >
                {connectors.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ color: "#fff", fontSize: 13 }}>
                    {c.client_name}
                    {c.id === connectorId ? " (you)" : ""}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        )}

        {/* Loaded simulator indicator */}
        <Box sx={{ px: "18px", py: "10px", mb: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Maven Pro, sans-serif",
              fontSize: 10,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1.2,
            }}
          >
            Loaded Simulator
          </Typography>
          <Typography
            sx={{
              fontFamily: "Maven Pro, sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              wordBreak: "break-word",
              lineHeight: 1.3,
              mt: "2px",
            }}
          >
            {loadedSimulatorName || "—"}
          </Typography>
        </Box>

        {/* Nav items */}
        <Box sx={{ flex: 1, px: "8px" }}>
          {NAV_ITEMS.map(({ label, path, icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              style={({ isActive }) => itemStyle(isActive)}
            >
              {icon}
              <span>{label}</span>
            </NavLink>
          ))}
        </Box>

        {/* User info — same structure as console UserSummary */}
        <div style={{ padding: "0 18px 24px", position: "relative" }}>
          <div style={{ position: "relative" }}>
            <Popover
              className="menuPopover"
              id="menu-popover"
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              PaperProps={{
                elevation: 0,
                sx: {
                  boxShadow: "5px -5px 5px 0px rgba(0,0,0,0.1), -5px -5px 5px 0px rgba(0,0,0,0.1) !important",
                },
              }}
              sx={{ transform: "translateX(25px)" }}
            >
              <Box
                aria-label="logout"
                onClick={() => { logout(); navigate("/login"); }}
                style={{ padding: "10px 40px", cursor: "pointer", position: "relative" }}
              >
                Log out
              </Box>
            </Popover>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 16, cursor: "pointer" }}
            onClick={(e) => setAnchorEl(e.currentTarget as HTMLDivElement)}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Maven Pro, sans-serif",
                fontWeight: 600,
                fontSize: 32,
                lineHeight: "20px",
                color: "#7166cf",
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div style={{ maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}>
              {isSuperAdmin && (
                <Typography sx={textStyle}>Super Admin</Typography>
              )}
              <Typography sx={textStyle}>{user?.email}</Typography>
              {activeConnectorId && (
                <Typography sx={textStyle}>{activeConnectorId}</Typography>
              )}
            </div>
          </div>
        </div>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{ flex: 1, ml: "232px", bgcolor: "#f8f9fa", minHeight: "100vh", overflow: "auto" }}
      >
        <Outlet key={activeConnectorId ?? "own"} />
      </Box>
    </Box>
  );
};

export default Layout;
